import React, {ReactElement, useEffect, useMemo, useRef, useState} from 'react'
import Plate from "../../Plate";
import UsersIcon from "../../../assets/icon/UsersIcon";
import SearchIcon from "../../../assets/icon/SearchIcon";
import DropDown from "../../DropDown";
import UserItemPicker from "./components/UserItemPicker";
import UsersAvatar from "../../UsersAvatar";
import {UserService} from "../../../services";
import WorkspaceMembersService from "../../../services/workspace-members.service";
import {useUser} from "cor-business";
import { useWorkspace } from "../../../context/workspace.context";
import {
    extractList,
    getUserId,
    getProjectMemberUserId,
    normalizeProjectMember,
    normalizeUserEntry,
    sortUsersBySelection,
    usersHaveSameIdentity,
} from "../../../utils/members";

import styles from './index.module.scss'

interface UsersPickerProps {
    getUsers: (usersId: string | number) => void
    usersId: (string | number)[]
    selectedUsers?: any[]
    availableUsers?: any[]
    projectId?: number
    onlyIcon?: boolean
    btn?: ReactElement
    dropdownPosition?: 'left' | 'right' | 'side-right'
    prioritizeSelected?: boolean
    openDownward?: boolean
    fixedDropdownHeight?: boolean
}

const userMatchesSearch = (user: any, search: string) => {
    const query = search.trim().toLowerCase()

    if (!query) {
        return true
    }

    return [
        user?.email,
        user?.first_name,
        user?.last_name,
        `${user?.first_name || ''} ${user?.last_name || ''}`,
    ].some((value) => typeof value === 'string' && value.toLowerCase().includes(query))
}

export default function UsersPicker({getUsers, usersId, selectedUsers = [], availableUsers, onlyIcon, projectId, btn, dropdownPosition = 'left', prioritizeSelected = false, openDownward = false, fixedDropdownHeight = false}: UsersPickerProps) {
    const [usersPickerList, setUsersPickerList] = useState<any[]>([])
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [searchValue, setSearchValue] = useState('')
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const {user} = useUser()
    const { workspace } = useWorkspace()
    const pickerUsers = useMemo(() => availableUsers
        ? availableUsers.filter((currentUser) => userMatchesSearch(currentUser, searchValue))
        : usersPickerList, [availableUsers, searchValue, usersPickerList])
    const pickedUsers = useMemo(() => pickerUsers.filter((user: any) => (
        usersId.some((userId) => String(userId) === String(user?.id ?? user?.user_id))
    )), [usersId, pickerUsers])
    const displayedPickedUsers = useMemo(() => {
        const selectedFromTask = selectedUsers.filter((selectedUser: any) => {
            const selectedUserId = getUserId(selectedUser)
            return selectedUserId && usersId.some((userId) => String(userId) === selectedUserId)
        })
        const seenIds = new Set<string>()

        return [...pickedUsers, ...selectedFromTask].filter((selectedUser: any, index) => {
            const selectedUserId = getUserId(selectedUser) || `selected-user-${index}`
            if (seenIds.has(selectedUserId)) return false
            seenIds.add(selectedUserId)
            return true
        })
    }, [pickedUsers, selectedUsers, usersId])
    const usersWithSelected = useMemo(() => {
        if (!prioritizeSelected) {
            return pickerUsers
        }

        const mergedUsers = [...pickerUsers]
        const knownUserIds = new Set(mergedUsers.map(getUserId).filter(Boolean))

        selectedUsers.forEach((selectedUser: any) => {
            const selectedUserId = getUserId(selectedUser)
            const normalizedSelectedUser = {
                ...(selectedUser?.user || {}),
                ...selectedUser,
                id: selectedUserId,
                user_id: selectedUserId,
            }
            if (
                !selectedUserId
                || knownUserIds.has(selectedUserId)
                || !userMatchesSearch(normalizedSelectedUser, searchValue)
            ) {
                return
            }

            mergedUsers.push(normalizedSelectedUser)
            knownUserIds.add(selectedUserId)
        })

        return sortUsersBySelection(mergedUsers, usersId)
    }, [prioritizeSelected, searchValue, selectedUsers, usersId, pickerUsers])
    const currentUserId = getUserId(user)
    const userMe = useMemo(() => {
        if (!currentUserId) {
            return null
        }

        const listedCurrentUser = usersWithSelected.find((currentUser: any) => (
            usersHaveSameIdentity(currentUser, user)
        ))

        // Project mutations must use the id returned by the project-member
        // list. Do not expose the auth-profile id while that list is loading:
        // selecting that temporary row makes the assignee API reject the save.
        if (projectId && !listedCurrentUser) {
            return null
        }

        return normalizeUserEntry(user, listedCurrentUser)
    }, [currentUserId, projectId, user, usersWithSelected])

    const fetchProjectMembers = async (search = '') => {
        if (!projectId || !workspace?.id) {
            return null
        }

        const [projectMembersData, workspaceMembersData] = await Promise.all([
            WorkspaceMembersService.getProjectMembers(Number(projectId)),
            WorkspaceMembersService.getMembers(Number(workspace.id)).catch(() => null),
        ])

        const workspaceMembersByUser = new Map<string, any>()
        extractList(workspaceMembersData).forEach((member) => {
            const userId = getUserId(member)
            if (userId) {
                workspaceMembersByUser.set(userId, member)
            }
        })

        return extractList(projectMembersData)
            .map((projectMember) => {
                const userId = getProjectMemberUserId(projectMember)
                return normalizeProjectMember(projectMember, workspaceMembersByUser.get(userId))
            })
            .filter(Boolean)
            .filter((currentUser) => userMatchesSearch(currentUser, search))
    }

    const fetchUsers = async (search = '') => {
        if (availableUsers) return
        try {
            const projectMembers = await fetchProjectMembers(search).catch(() => null)

            if (projectMembers) {
                setUsersPickerList(projectMembers)
                return
            }

            const userList = await UserService.getUsers({
                skip: 0,
                limit: 1000,
                ...(search ? {search} : {}),
                ...(projectId ? {project_id: projectId} : {})
            })

            setUsersPickerList(extractList(userList))
        } catch (e) {
            console.error('Failed to fetch users:', e)
            setUsersPickerList([])
        }
    }

    const handleSearchChange = (value: string) => {
        setSearchValue(value)

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }

        if (!availableUsers) {
            searchTimerRef.current = setTimeout(() => {
                fetchUsers(value)
            }, 300)
        }
    }

    useEffect(() => {
        if (!availableUsers) fetchUsers()

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current)
            }
        }
    }, [projectId, workspace?.id, availableUsers])

    const defaultBtn = useMemo(() => {
        if (displayedPickedUsers.length) {
            return <UsersAvatar avatarsCount={4} users={displayedPickedUsers}/>
        }

        if (onlyIcon) {
            return <UsersIcon color={isDropdownOpen ? '#64617E' : undefined}/>
        }

        if (btn) {
            return btn
        }

        return (
            <Plate title={"Участники"}
                   active={isDropdownOpen}
                   icon={<UsersIcon color={isDropdownOpen ? '#64617E' : undefined}/>}/>
        )
    }, [displayedPickedUsers, onlyIcon, btn, isDropdownOpen])

    const visibleUsers = useMemo(() => {
        if (!userMe) {
            return usersWithSelected
        }

        return usersWithSelected.filter((currentUser: any) => getUserId(currentUser) !== getUserId(userMe))
    }, [usersWithSelected, userMe])

    const showMeRow = Boolean(userMe) && userMatchesSearch(userMe, searchValue)

    return (
        <div className={styles.usersPicker}>
            <DropDown
                position={dropdownPosition}
                isOpen={isDropdownOpen}
                setIsOpen={setIsDropdownOpen}
                verticalPlacement={openDownward ? 'bottom' : 'auto'}
                contentClassName={fixedDropdownHeight ? styles.usersPicker__dropdownContent : undefined}
                btn={defaultBtn}>
                <div className={styles.usersPicker__menu}>
                    {showMeRow && (
                        <div className={styles.usersPicker__currentUser}>
                            <UserItemPicker user={userMe} getUsers={getUsers}
                                            isActive={usersId.some((userId) => String(userId) === String(userMe?.id))}/>
                        </div>
                    )}
                    <div className={styles.usersPicker__search}>
                        <SearchIcon/>
                        <input
                            type="text"
                            className={styles.usersPicker__searchInput}
                            value={searchValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder="Поиск по имени или email"
                        />
                    </div>
                    <div className={styles.usersPicker__list}>
                        {visibleUsers.map((user: any) => (
                            <UserItemPicker key={user.id} user={user} getUsers={getUsers}
                                            isActive={usersId.some((userId) => String(userId) === String(user.id))}/>
                        ))}
                        {visibleUsers.length === 0 && !showMeRow && (
                            <div className={styles.usersPicker__empty}>Ничего не найдено</div>
                        )}
                    </div>
                </div>
            </DropDown>
        </div>
    )
}
